/**
 * 「从后台清单里选」的**唯一**下拉实现（v0.61）。
 *
 * 三处消费方（气泡图页的检测工具、指导书页的量具/频率、零件主数据的 5 个字段）
 * 全都走这里 —— 否则「表外历史值要不要保留」「清单为空怎么提示」这些规则会
 * 各写一遍，然后就各不一样了。
 *
 * ★ 三条硬规则（每条都有用例/探针守）：
 *   1. **严格下拉**：只能从清单里选（`<select>`，不能手输）。
 *   2. **表外历史值原样保留**：后端 `method` 等字段历史上是自由文本，库里存着
 *      「塞规」这类早已不在候选表里的值。若下拉把它吃掉，受控 `<select>` 会显示
 *      成第一项 ⇒ **静默改值**，比不显示更危险。所以把它补进选项并标注「表外」。
 *   3. **清单为空要说话**：`模具类型 / 模穴 / 冲床吨位` 出厂就是空的。此时禁用
 *      控件并给出「去设置里加」的明确提示，而不是让人对着一个空下拉发呆。
 */
import React from 'react';

export interface OptionSelectProps {
  /** 可选值（来自 `useOptionValues(kind)`） */
  opts: string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** 空值的显示文案（默认「—」）。设为 `false` 表示不允许清空。 */
  emptyLabel?: string | false;
  /** 清单为空时的提示（默认引导去设置页） */
  emptyHint?: string;
  className?: string;
  title?: string;
  /** 无值时的兜底提示（用于 `title`） */
  placeholderTitle?: string;
}

/** 当前值不在清单里时，标注用的后缀（**只影响显示**，提交的仍是原值）。 */
export const OFF_LIST_SUFFIX = '（表外值）';

export function OptionSelect({
  opts, value, onChange, disabled = false, emptyLabel = '—',
  emptyHint = '清单为空 —— 到「设置 · 量具与选项」里先加一项',
  className = '', title, placeholderTitle,
}: OptionSelectProps) {
  const list = (opts ?? []).filter((s) => String(s ?? '').trim() !== '');
  const empty = list.length === 0;
  const offList = !!value && !list.includes(value);
  const finalOpts = offList ? [value, ...list] : list;

  if (empty && !value) {
    return (
      <select className={className} disabled value=""
              title={title || emptyHint}
              onChange={() => { /* disabled，不会触发 */ }}>
        <option value="">{emptyHint}</option>
      </select>
    );
  }

  return (
    <select className={className}
            value={value}
            disabled={disabled}
            title={title || (offList
              ? `「${value}」已不在后台清单里 —— 这里原样保留，不会被改掉；`
                + '如仍在用请到设置页把它加回去'
              : (empty ? emptyHint : placeholderTitle))}
            onChange={(e) => onChange(e.target.value)}>
      {emptyLabel !== false && <option value="">{emptyLabel}</option>}
      {finalOpts.map((o) => (
        <option key={o} value={o}>
          {offList && o === value ? `${o}${OFF_LIST_SUFFIX}` : o}
        </option>
      ))}
    </select>
  );
}
